import React from 'react';
import { Typography, Tooltip } from 'antd';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  FireOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;

// Statuses that should show overdue indicator
const OVERDUE_ELIGIBLE_STATUSES = ['PENDING', 'SCHEDULED', 'RESCHEDULED'];

export const useScheduledDateRender = () => {
  return React.useCallback((scheduledDate: any, status?: string) => {
    if (!scheduledDate) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 6px',
            borderRadius: 4,
            backgroundColor: '#f5f5f5',
            border: '1px solid #d9d9d9',
          }}
        >
          <CalendarOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
          <Text
            style={{
              color: '#8c8c8c',
              fontSize: '11px',
              fontWeight: 500,
              margin: 0,
            }}
          >
            Not scheduled
          </Text>
        </div>
      );
    }

    const date = dayjs(scheduledDate);
    const now = dayjs();
    const timeDiffHours = date.diff(now, 'hour');
    const timeDiffMinutes = date.diff(now, 'minute');
    const isOverdue = date.isBefore(now);
    const isToday = date.isSame(now, 'day');
    const isTomorrow = date.isSame(now.add(1, 'day'), 'day');
    const isThisWeek = date.isSame(now, 'week') && !isToday && !isTomorrow;
    const isNextWeek = date.isSame(now.add(1, 'week'), 'week');
    const isUrgent = isToday && timeDiffMinutes <= 120 && timeDiffMinutes > 0; // 2 hours = 120 minutes

    // Check if status is eligible for overdue indicator
    const shouldShowOverdue =
      status && OVERDUE_ELIGIBLE_STATUSES.includes(status);

    const tooltipContent = (
      <div>
        <div style={{ fontWeight: 500 }}>Scheduled Date & Time</div>
        <div>{date.format('dddd, MMMM DD, YYYY')}</div>
        <div>{date.format('hh:mm A')}</div>
        {isOverdue && shouldShowOverdue && (
          <div style={{ color: '#ff4d4f', marginTop: 4 }}>
            ⚠️ Overdue by{' '}
            {Math.abs(timeDiffMinutes) < 1
              ? 'less than 1 minute'
              : Math.abs(timeDiffMinutes) < 60
                ? `${Math.abs(timeDiffMinutes)} minutes`
                : `${Math.abs(timeDiffHours)} hours`}
          </div>
        )}
      </div>
    );

    // Overdue items - Highest priority (only for eligible statuses)
    if (isOverdue && shouldShowOverdue) {
      const minutesOverdue = Math.abs(timeDiffMinutes);
      const hoursOverdue = Math.abs(timeDiffHours);

      let overdueText;
      if (minutesOverdue < 1) {
        // Show "just now" for items overdue less than 1 minute
        overdueText = 'Just now';
      } else if (minutesOverdue < 60) {
        // Show minutes for items overdue less than 1 hour
        overdueText = `${minutesOverdue}m overdue`;
      } else if (hoursOverdue < 24) {
        // Show hours for items overdue less than 1 day
        overdueText = `${Math.floor(hoursOverdue)}h overdue`;
      } else {
        // Show days for items overdue more than 1 day
        overdueText = `${Math.floor(hoursOverdue / 24)}d overdue`;
      }

      return (
        <Tooltip title={tooltipContent} placement="top">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '3px 8px',
              borderRadius: 6,
              backgroundColor: '#fff2f0',
              border: '1px solid #ffb3b3',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <FireOutlined style={{ color: '#ff4d4f', fontSize: '14px' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <Text
                style={{
                  color: '#ff4d4f',
                  fontSize: '11px',
                  fontWeight: 600,
                  margin: 0,
                  lineHeight: '1.1',
                }}
              >
                {overdueText}
              </Text>
              <Text
                style={{
                  color: '#666',
                  fontSize: '10px',
                  margin: 0,
                  lineHeight: '1.1',
                }}
              >
                {date.format('MMM DD, hh:mm A')}
              </Text>
            </div>
          </div>
        </Tooltip>
      );
    }

    // Today's items - Medium priority
    if (isToday) {
      const minutesUntil = Math.floor(timeDiffMinutes);
      const hoursUntil = Math.floor(timeDiffHours);

      let timeText;
      if (minutesUntil <= 0) {
        timeText = 'Now';
      } else if (minutesUntil < 1) {
        // Show "now" for items due within 1 minute
        timeText = 'Now';
      } else if (minutesUntil < 60) {
        // Show minutes for items within the next hour
        timeText = `In ${minutesUntil}m`;
      } else if (hoursUntil <= 2) {
        // Show hours for items within the next 2 hours
        timeText = `In ${hoursUntil}h`;
      } else {
        timeText = 'Today';
      }

      const isUrgentStyle = isUrgent
        ? {
            backgroundColor: '#fff7e6',
            border: '1px solid #ffd591',
          }
        : {
            backgroundColor: '#e6f7ff',
            border: '1px solid #91d5ff',
          };

      const isUrgentColor = isUrgent ? '#d46b08' : '#1890ff';

      return (
        <Tooltip title={tooltipContent} placement="top">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '3px 8px',
              borderRadius: 6,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              ...isUrgentStyle,
            }}
          >
            {isUrgent ? (
              <FireOutlined
                style={{ color: isUrgentColor, fontSize: '14px' }}
              />
            ) : (
              <ClockCircleOutlined
                style={{ color: isUrgentColor, fontSize: '14px' }}
              />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <Text
                style={{
                  color: isUrgentColor,
                  fontSize: '11px',
                  fontWeight: 600,
                  margin: 0,
                  lineHeight: '1.1',
                }}
              >
                {timeText}
              </Text>
              <Text
                style={{
                  color: '#666',
                  fontSize: '10px',
                  margin: 0,
                  lineHeight: '1.1',
                }}
              >
                {date.format('MMM DD, hh:mm A')}
              </Text>
            </div>
          </div>
        </Tooltip>
      );
    }

    // Tomorrow's items
    if (isTomorrow) {
      return (
        <Tooltip title={tooltipContent} placement="top">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '3px 8px',
              borderRadius: 6,
              backgroundColor: '#f6ffed',
              border: '1px solid #b7eb8f',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <CalendarOutlined style={{ color: '#52c41a', fontSize: '14px' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <Text
                style={{
                  color: '#52c41a',
                  fontSize: '11px',
                  fontWeight: 600,
                  margin: 0,
                  lineHeight: '1.1',
                }}
              >
                Tomorrow
              </Text>
              <Text
                style={{
                  color: '#666',
                  fontSize: '10px',
                  margin: 0,
                  lineHeight: '1.1',
                }}
              >
                {date.format('MMM DD, hh:mm A')}
              </Text>
            </div>
          </div>
        </Tooltip>
      );
    }

    // This week's items
    if (isThisWeek) {
      return (
        <Tooltip title={tooltipContent} placement="top">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '3px 8px',
              borderRadius: 6,
              backgroundColor: '#f0f5ff',
              border: '1px solid #adc6ff',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <CalendarOutlined style={{ color: '#722ed1', fontSize: '14px' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <Text
                style={{
                  color: '#722ed1',
                  fontSize: '11px',
                  fontWeight: 600,
                  margin: 0,
                  lineHeight: '1.1',
                }}
              >
                {date.format('ddd, MMM DD')}
              </Text>
              <Text
                style={{
                  color: '#666',
                  fontSize: '10px',
                  margin: 0,
                  lineHeight: '1.1',
                }}
              >
                {date.format('hh:mm A')}
              </Text>
            </div>
          </div>
        </Tooltip>
      );
    }

    // Next week's items
    if (isNextWeek) {
      return (
        <Tooltip title={tooltipContent} placement="top">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '3px 8px',
              borderRadius: 6,
              backgroundColor: '#fafafa',
              border: '1px solid #d9d9d9',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <CalendarOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <Text
                style={{
                  color: '#8c8c8c',
                  fontSize: '11px',
                  fontWeight: 600,
                  margin: 0,
                  lineHeight: '1.1',
                }}
              >
                {date.format('MMM DD')}
              </Text>
              <Text
                style={{
                  color: '#666',
                  fontSize: '10px',
                  margin: 0,
                  lineHeight: '1.1',
                }}
              >
                {date.format('hh:mm A')}
              </Text>
            </div>
          </div>
        </Tooltip>
      );
    }

    // Future dates
    return (
      <Tooltip title={tooltipContent} placement="top">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '3px 8px',
            borderRadius: 6,
            backgroundColor: '#fafafa',
            border: '1px solid #d9d9d9',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
          }}
        >
          <CalendarOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <Text
              style={{
                color: '#262626',
                fontSize: '11px',
                fontWeight: 600,
                margin: 0,
                lineHeight: '1.1',
              }}
            >
              {date.format('MMM DD, YYYY')}
            </Text>
            <Text
              style={{
                color: '#666',
                fontSize: '10px',
                margin: 0,
                lineHeight: '1.1',
              }}
            >
              {date.format('hh:mm A')}
            </Text>
          </div>
        </div>
      </Tooltip>
    );
  }, []);
};
